/**
 * Client voice telemetry (004-p7 Track A3.4 — ANA-644 / M7).
 *
 * Voice-connect failures are CLIENT-side and invisible to server logs (plan-review
 * M7). This is the thin, pluggable sink that makes them observable — the instrument
 * for the reconnect-loop SPIKE (ANA-644) and the M7 precondition.
 *
 * ── Sink (coordinated with #1105 / spec 043 client PostHog) ────────────────────
 * There is deliberately NO analytics SDK imported here. Rhys (2026-07-11) chose to
 * consume #1105's client PostHog rather than stand up a second sink. When that
 * bootstrap lands it calls {@link setVoiceTelemetrySink} once (e.g. with
 * `(e, p) => posthog.capture(e, p)`), and every voice event routes there.
 *
 * Until then (and whenever the sink is dark — no PostHog key on staging) events
 * fall back to a structured `console` breadcrumb, so the spike is NEVER fully blind
 * — the exact failure mode (a warn nobody heard for a month) this whole phase closes.
 *
 * Fire-and-forget + swallow-all: a telemetry failure must NEVER affect the voice
 * connect flow. All emits are wrapped; the sink is only ever called from here.
 */

export type VoiceTelemetryProps = Record<
  string,
  string | number | boolean | null | undefined
>;

export type VoiceTelemetrySink = (event: string, props?: VoiceTelemetryProps) => void;

let sink: VoiceTelemetrySink | null = null;

/**
 * Install the real telemetry sink (called ONCE by the app-level client-analytics
 * bootstrap — #1105 / spec 043). Passing `null` clears it (back to the console
 * fallback). Idempotent-friendly: the last caller wins.
 */
export function setVoiceTelemetrySink(next: VoiceTelemetrySink | null): void {
  sink = next;
}

/** True when a real sink is installed (else emits go to the console fallback). */
export function hasVoiceTelemetrySink(): boolean {
  return sink != null;
}

/**
 * Emit a voice telemetry event. Routes to the installed sink, else a structured
 * `console.warn` breadcrumb (dark-sink fallback — the spike stays observable on
 * staging even before #1105 lands). Never throws.
 */
export function captureVoiceEvent(event: string, props?: VoiceTelemetryProps): void {
  try {
    if (sink) {
      sink(event, props);
    } else {
      // Dark-sink breadcrumb — greppable in the browser console + any log capture.
      // eslint-disable-next-line no-console
      console.warn(`[voice-telemetry] ${event}`, props ?? {});
    }
  } catch {
    // Telemetry must never affect the connect flow — swallow everything.
  }
}

/**
 * Best-effort `document.visibilityState` (the tab-visibility discriminator for the
 * ANA-644 reconnect-loop hypothesis). `unknown` when `document` is absent (SSR/tests).
 */
export function currentVisibilityState(): string {
  try {
    return typeof document !== 'undefined' && document.visibilityState
      ? document.visibilityState
      : 'unknown';
  } catch {
    return 'unknown';
  }
}
